import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { uploadImageToCloudinary } from '../services/cloudinaryService';

// Profile data interface
export interface ProfileData {
    id: string;
    fullName: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    bio: string;
    shortBio: string;
    photoURL: string;
    skills: {
        id: number;
        category: string;
        items?: string[]; // Making items optional for backward compatibility
        subcategories?: {
            name: string;
            skills: string[];
        }[];
    }[];
    approachItems: {
        id: number;
        title: string;
        description: string;
    }[];
    resumeSummary: string;
    skillsDescription: string;
    projectsDescription: string;
    socialLinks: {
        linkedin: string;
        github: string;
        twitter?: string;
        website?: string;
        googleScholar?: string;
        researchGate?: string;
        orcid?: string;
    };
    // New fields for academic audiences
    researchInterests: {
        id: number;
        title: string;
        description: string;
    }[];
    teachingExperience: {
        id: number;
        title: string;
        description: string;
    }[];
    // New fields for industry recruiters
    softSkills: {
        id: number;
        title: string;
        description: string;
    }[];
    industryKnowledge: string[];
    certifications: {
        id: number;
        name: string;
        issuer: string;
        date: string;
        url?: string;
        imageUrl?: string;
    }[];
    participations: {
        id: number;
        name: string;
        issuer: string;
        date: string;
        url?: string;
    }[];
    languages: {
        id: number;
        name: string;
        proficiency: string;
    }[];
    // New fields for both audiences
    personalStatement: string;
    researchSummary: string;
    mentorshipExperience: string;
    // Badge/highlight images
    pmpBadgeUrl?: string;
    lastUpdated: any;
}

// Default profile data
export const defaultProfile: ProfileData = {
    id: 'main-profile',
    fullName: 'Tarun Kumar Biswas',
    title: 'Software Development Manager | Mobile Engineering Leader',
    email: 'tarun.pstu@gmail.com',
    phone: '+880 1712 335833',
    location: 'Mogbazar, Dhaka, Bangladesh',
    bio: 'Software Development Manager with 13+ years across mobile, web, backend, and AI/ML, the last 5+ years in engineering leadership. Began as a hands-on mobile engineer shipping native iOS, native Android, and Flutter applications, then advanced to Mobile App Development Team Lead and Development Manager, directing cross-functional teams of up to 30 engineers in fintech, e-learning, health, ride-hailing, and AI/ML products, including international Japanese-enterprise apps.',
    shortBio: 'Software Development Manager & Mobile Engineering Leader with 13+ years of experience',
    photoURL: 'https://placehold.co/300x300/EEE/31343C',
    skills: [
        {
            id: 1,
            category: 'Engineering Leadership',
            items: ['Team Management (30+ Engineers)', 'Hiring & Mentoring', 'Code Review Standards', 'Release Management', 'Agile / Scrum', 'Sprint Planning', 'Roadmap & Stakeholder Management'],
        },
        {
            id: 2,
            category: 'Languages',
            items: ['Swift', 'Kotlin', 'Java', 'Objective-C', 'Dart', 'SQL', 'C/C++'],
        },
        {
            id: 3,
            category: 'Mobile Platforms',
            items: ['Native iOS (UIKit, SwiftUI)', 'Native Android (Android SDK, Jetpack)', 'Flutter (Riverpod, freezed, go_router, dio)'],
        },
        {
            id: 4,
            category: 'Architecture',
            items: ['MVVM', 'MVVM-C', 'VIPER', 'SOLID Principles', 'Clean Architecture', 'Dependency Injection'],
        },
        {
            id: 5,
            category: 'Cloud & DevOps',
            items: ['AWS (EC2, S3, RDS, SES, Route 53, VPC)', 'Firebase Suite', 'CI/CD', 'Fastlane', 'GitHub Actions', 'Bunny CDN', 'Redis'],
        },
        {
            id: 6,
            category: 'Testing & Quality',
            items: ['TDD', 'XCTest', 'JUnit', 'Espresso', 'Code Coverage', 'Static Analysis', 'Proguard / Dexguard'],
        },
        {
            id: 7,
            category: 'Data & Backend',
            items: ['REST APIs', 'Socket.IO', 'SQLite', 'Room', 'Core Data', 'MongoDB', 'PostgreSQL', 'MySQL'],
        },
        {
            id: 8,
            category: 'Payments & Auth',
            items: ['Google Play Billing', 'Stripe', 'SSLCommerz', 'BKash', 'JWT', 'Biometric Auth', 'OAuth (Google / Apple / Facebook)'],
        },
    ],
    approachItems: [
        {
            id: 1,
            title: 'Engineering Leadership',
            description: 'Building, mentoring, and scaling cross-functional teams while owning architecture and release decisions end to end.'
        },
        {
            id: 2,
            title: 'Clean Architecture',
            description: 'Driving MVVM / Clean Architecture and SOLID principles to keep products maintainable and scalable at team scale.'
        },
        {
            id: 3,
            title: 'Quality & Automation',
            description: 'Institutionalizing code reviews, CI/CD pipelines, and automated test gates to reduce regressions and crash rates.'
        },
        {
            id: 4,
            title: 'Cross-Platform Delivery',
            description: 'Shipping iOS and Android from native and Flutter codebases, balancing speed, performance, and product goals.'
        }
    ],
    resumeSummary: 'Software Development Manager with 13+ years across mobile, web, backend, and AI/ML, the last 5+ years in engineering leadership. Began as a hands-on mobile engineer shipping native iOS, native Android, and Flutter applications, then advanced to Mobile App Development Team Lead and Development Manager, directing cross-functional teams of up to 30 engineers across the full stack (mobile, web, backend, QA, AI/ML) in fintech, e-learning, health, ride-hailing, and AI/ML products, including international Japanese-enterprise apps. Grew products to 500K+ active users, lifted unit-test coverage from 30% to 60%, and cut load times by 50%. Fluent in MVVM/SOLID architecture, CI/CD automation, and cloud platforms on AWS and Firebase.',
    skillsDescription: 'A leadership and technical toolkit spanning native iOS/Android, Flutter, scalable architecture, cloud, and team delivery practices built over 13+ years.',
    projectsDescription: 'Selected products and platforms I have led or delivered across e-learning, marketplace, AI/ML, and enterprise mobile.',
    socialLinks: {
        linkedin: 'https://www.linkedin.com/in/tarunbiswas/',
        github: 'https://github.com/taruncse',
        twitter: '',
        website: 'https://tarunbiswas.com',
        googleScholar: '',
        researchGate: '',
        orcid: '',
    },
    // Default values for new academic fields
    researchInterests: [
        {
            id: 1,
            title: "Network Intrusion Detection",
            description: "Lightweight temporal-spatial transformer approaches for intrusion detection in drone and IoT networks."
        },
        {
            id: 2,
            title: "Computer Vision & AI/ML",
            description: "Facial recognition (liveness and expression detection) and YOLO-based object-flow systems for real-world products."
        }
    ],
    teachingExperience: [],
    // Default values for new recruiter-focused fields
    softSkills: [
        {
            id: 1,
            title: "Team Leadership",
            description: "Building, hiring, and mentoring cross-functional teams of up to 30 engineers across the full stack."
        },
        {
            id: 2,
            title: "Stakeholder Management",
            description: "Aligning roadmaps, release planning, and executive reporting with business objectives across multiple products."
        },
        {
            id: 3,
            title: "Delivery & Execution",
            description: "Owning sprint planning, code review standards, and release management to ship reliably on schedule."
        }
    ],
    industryKnowledge: ["Fintech", "E-Learning", "Health", "Ride-Hailing", "AI/ML", "Enterprise Mobile (Japanese Market)"],
    certifications: [],
    participations: [
        {
            id: 1,
            name: "Mobile World Congress (MWC) 2015, Barcelona — Company Representative",
            issuer: "W3Engineers / GSMA",
            date: "2015",
            url: ""
        },
        {
            id: 2,
            name: "ACM NCPC 2010 — Programming Contest Team Leader",
            issuer: "ACM",
            date: "2010",
            url: ""
        }
    ],
    languages: [
        {
            id: 1,
            name: "English",
            proficiency: "Professional"
        },
        {
            id: 2,
            name: "Bengali",
            proficiency: "Native"
        }
    ],
    // New fields for both audiences
    personalStatement: "I lead engineering teams that turn ambitious product ideas into reliable, well-architected software. After 13+ years from hands-on mobile engineer to Senior Development Manager, I care most about growing people, raising quality bars, and shipping products that reach users at scale.",
    researchSummary: "Co-author of \"A Novel Unified Lightweight Temporal-Spatial Transformer Approach for Intrusion Detection in Drone Networks\" published in Scientific Reports (Nature), with applied work in computer vision and AI/ML identity verification.",
    mentorshipExperience: "Mentored and grew mobile and full-stack engineers across multiple teams, establishing code-review standards, TDD practices, and hiring processes that raised unit-test coverage from 30% to 60%.",
    lastUpdated: null,
};

const PROFILE_DOC_ID = 'main-profile';

// Get profile data
export const getProfile = async (): Promise<ProfileData> => {
    try {
        const docRef = doc(db, 'profile', PROFILE_DOC_ID);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data() as ProfileData;
        } else {
            // If profile doesn't exist, create it with default data
            await setDoc(docRef, {
                ...defaultProfile,
                lastUpdated: serverTimestamp(),
            });
            return defaultProfile;
        }
    } catch (error) {
        console.error('Error getting profile:', error);
        throw error;
    }
};

// Update profile data
export const updateProfile = async (profileData: Partial<ProfileData>): Promise<void> => {
    try {
        const docRef = doc(db, 'profile', PROFILE_DOC_ID);
        await updateDoc(docRef, {
            ...profileData,
            lastUpdated: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error updating profile:', error);
        throw error;
    }
};

// Upload profile image using Cloudinary
export const uploadProfileImage = async (
    file: File,
    progressCallback?: (progress: number) => void
): Promise<string> => {
    try {
        // Use Cloudinary service to upload the file
        const imageUrl = await uploadImageToCloudinary(file, progressCallback);
        return imageUrl;
    } catch (error) {
        console.error('Error uploading image to Cloudinary:', error);
        throw error;
    }
};

// Delete profile image (Note: Client-side Cloudinary deletion is not possible without exposing API credentials)
export const deleteProfileImage = async (imageUrl: string): Promise<void> => {
    // For Cloudinary free tier, we'll simply replace the old image when uploading a new one
    // This is a safe approach for a portfolio site
    console.log('Old image will be replaced with new upload', imageUrl);

    // If deletion is critical in the future, you would need a server-side solution
    // For now, we just return without error as no action is needed client-side
    return Promise.resolve();
};